/**
 * erc-ux.js — capa de experiencia de usuario de Exotic (ver snippets/erc-ux.liquid).
 *
 * Lee la configuración de window.ercUx (la imprime el snippet según los ajustes
 * del tema) y activa solo lo que esté encendido:
 *   progress   barra de progreso de scroll ('all' | 'article' | 'none')
 *   top        botón volver arriba con anillo de progreso
 *   loadbar    barra de carga al navegar entre páginas internas
 *   imgFade    fundido de imágenes diferidas al cargar
 *   reveal     revelado de secciones que Dawn no anima (.scroll-trigger)
 *   cursor     anillo que sigue al puntero (solo ratón)
 *   ripple     onda al pulsar botones
 *   theme      interruptor oscuro/claro (preferencia en localStorage 'erc-theme')
 *
 * Eventos al dataLayer (vía window.ercTrack de erc-ga-events.js):
 *   ui_interaction  category 'ux'  action theme-toggle | back-to-top  label …
 */
(function () {
  'use strict';

  var cfg = window.ercUx || {};
  var root = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function track(name, action, label) {
    if (typeof window.ercTrack === 'function') {
      window.ercTrack(name, 'ux', label, { event_action: action });
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, event_category: 'ux', event_action: action, event_label: label });
    }
  }

  function store(key, value) {
    try {
      if (value === undefined) return localStorage.getItem(key);
      localStorage.setItem(key, value);
    } catch (e) {
      return null;
    }
  }

  // ------------------------------------------------------------------
  // Scroll: barra de progreso + botón volver arriba (un solo listener)
  // ------------------------------------------------------------------
  var progressEl = null;
  var topEl = document.querySelector('[data-erc-top]');
  var topRing = topEl && topEl.querySelector('circle');

  if (cfg.progress === 'all' || (cfg.progress === 'article' && document.body.dataset.pageType === 'article')) {
    progressEl = document.querySelector('[data-erc-progress]');
  }

  if (progressEl || topEl) {
    var ticking = false;

    var onScroll = function () {
      ticking = false;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var y = window.scrollY || window.pageYOffset;
      var ratio = max > 0 ? Math.min(Math.max(y / max, 0), 1) : 0;

      if (progressEl) progressEl.style.transform = 'scaleX(' + ratio + ')';
      if (topEl) {
        topEl.classList.toggle('is-visible', y > window.innerHeight * 0.8);
        if (topRing) topRing.style.strokeDashoffset = String(100 - ratio * 100);
      }
    };

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(onScroll);
        }
      },
      { passive: true }
    );
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  }

  if (topEl) {
    topEl.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      var skip = document.getElementById('MainContent');
      if (skip) skip.focus({ preventScroll: true });
      track('ui_interaction', 'back-to-top', document.body.dataset.pageType || '');
    });
  }

  // ------------------------------------------------------------------
  // Barra de carga al salir hacia otra página interna
  // ------------------------------------------------------------------
  var loadbar = cfg.loadbar && document.querySelector('[data-erc-loadbar]');

  if (loadbar) {
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      var url;
      try {
        url = new URL(a.href, window.location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      loadbar.classList.add('is-loading');
    });

    // Volver con el botón "atrás" (bfcache) deja la barra a medias: reiniciarla.
    window.addEventListener('pageshow', function () {
      loadbar.classList.remove('is-loading');
    });
  }

  // ------------------------------------------------------------------
  // Fundido de imágenes diferidas (animación sin estado oculto previo)
  // ------------------------------------------------------------------
  if (cfg.imgFade && !reduceMotion) {
    var fadeImg = function (img) {
      if (img.complete || img.dataset.ercFade) return;
      img.dataset.ercFade = '1';
      img.addEventListener(
        'load',
        function () {
          img.classList.add('erc-img-in');
        },
        { once: true }
      );
    };
    document.querySelectorAll('#MainContent img[loading="lazy"]').forEach(fadeImg);
  }

  // ------------------------------------------------------------------
  // Revelado de secciones: solo las que Dawn no anima y que están
  // por debajo del pliegue al cargar (no toca el LCP ni lo visible).
  // ------------------------------------------------------------------
  if (cfg.reveal && !reduceMotion && !designMode && 'IntersectionObserver' in window) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          io.unobserve(el);
          el.classList.add('is-in');
          // Quitar las clases al terminar: un transform residual rompería
          // position: fixed/sticky de modales dentro de la sección.
          setTimeout(function () {
            el.classList.remove('erc-reveal', 'is-in');
          }, 900);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    document.querySelectorAll('#MainContent > .shopify-section').forEach(function (section) {
      if (section.querySelector('.scroll-trigger')) return;
      if (section.getBoundingClientRect().top < window.innerHeight) return;
      section.classList.add('erc-reveal');
      io.observe(section);
    });
  }

  // ------------------------------------------------------------------
  // Cursor: anillo que sigue al puntero (solo ratón / trackpad)
  // ------------------------------------------------------------------
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var INTERACTIVE = 'a[href], button, [role="button"], input, select, textarea, label, summary';

  if (cfg.cursor && finePointer && !reduceMotion && !designMode) {
    var cursor = document.createElement('div');
    cursor.className = 'erc-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    document.body.appendChild(cursor);

    var mx = 0;
    var my = 0;
    var cx = 0;
    var cy = 0;
    var running = false;

    var loop = function () {
      cx += (mx - cx) * 0.22;
      cy += (my - cy) * 0.22;
      cursor.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0)';
      if (Math.abs(mx - cx) > 0.1 || Math.abs(my - cy) > 0.1) {
        window.requestAnimationFrame(loop);
      } else {
        running = false;
      }
    };

    document.addEventListener(
      'pointermove',
      function (e) {
        if (e.pointerType !== 'mouse') return;
        mx = e.clientX;
        my = e.clientY;
        if (!cursor.classList.contains('is-active')) {
          cx = mx;
          cy = my;
          cursor.classList.add('is-active');
        }
        cursor.classList.toggle('is-hover', !!(e.target.closest && e.target.closest(INTERACTIVE)));
        if (!running) {
          running = true;
          window.requestAnimationFrame(loop);
        }
      },
      { passive: true }
    );
    document.addEventListener('pointerdown', function () {
      cursor.classList.add('is-down');
    });
    document.addEventListener('pointerup', function () {
      cursor.classList.remove('is-down');
    });
    document.documentElement.addEventListener('mouseleave', function () {
      cursor.classList.remove('is-active');
    });
  }

  // ------------------------------------------------------------------
  // Ripple al pulsar botones
  // ------------------------------------------------------------------
  var RIPPLE = '.button, .rental-cta, .err404__btn, .erc-mobile-cta__btn, .erc-header-cta, .erc-pbar__btn';

  if (cfg.ripple && !reduceMotion) {
    document.addEventListener(
      'pointerdown',
      function (e) {
        var btn = e.target.closest(RIPPLE);
        if (!btn || btn.disabled) return;

        if (window.getComputedStyle(btn).position === 'static') btn.style.position = 'relative';

        var host = btn.querySelector(':scope > .erc-ripple-host');
        if (!host) {
          host = document.createElement('span');
          host.className = 'erc-ripple-host';
          host.setAttribute('aria-hidden', 'true');
          btn.appendChild(host);
        }

        var rect = btn.getBoundingClientRect();
        var size = Math.max(rect.width, rect.height) * 2.2;
        var dot = document.createElement('span');
        dot.className = 'erc-ripple';
        dot.style.width = dot.style.height = size + 'px';
        dot.style.left = e.clientX - rect.left - size / 2 + 'px';
        dot.style.top = e.clientY - rect.top - size / 2 + 'px';
        host.appendChild(dot);
        dot.addEventListener('animationend', function () {
          dot.remove();
        });
      },
      { passive: true }
    );
  }

  if (cfg.ripple || cfg.cursor) root.classList.add('erc-fx');

  // ------------------------------------------------------------------
  // Interruptor oscuro / claro
  // ------------------------------------------------------------------
  var toggles = document.querySelectorAll('[data-erc-theme-toggle]');

  var syncToggles = function () {
    var light = root.getAttribute('data-theme') === 'light';
    toggles.forEach(function (btn) {
      btn.setAttribute('aria-pressed', light ? 'true' : 'false');
      var label = light ? btn.dataset.labelDark : btn.dataset.labelLight;
      btn.setAttribute('aria-label', label);
      btn.setAttribute('title', label);
    });
  };

  if (cfg.theme && toggles.length) {
    syncToggles();
    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var next = root.getAttribute('data-theme') === 'light' ? 'dark' : 'light';
        if (next === 'light') {
          root.setAttribute('data-theme', 'light');
        } else {
          root.removeAttribute('data-theme');
        }
        store('erc-theme', next);
        syncToggles();
        track('ui_interaction', 'theme-toggle', next);
      });
    });
  }

  // ------------------------------------------------------------------
  // Barra CTA de la ficha de producto. Móvil: siempre visible. Escritorio:
  // aparece cuando el botón de reserva de la ficha sale por arriba.
  // El botón de reserva puede ser:
  //   - el de Appointo (<div id="appointo-btn">, "revisar disponibilidad"):
  //     la app lo inserta en lugar del botón de compra (ELIMINA el original)
  //     y su onclick abre directamente el modal de disponibilidad. Como carga
  //     después, el objetivo se vuelve a buscar cuando cambia el formulario;
  //   - o el botón normal de Dawn (.product-form__submit[name="add"]).
  // "Reservar" pulsa ese botón: con Appointo abre el modal sin mover la página.
  // ------------------------------------------------------------------
  var pbar = document.querySelector('[data-erc-pbar]');

  if (pbar) {
    var reserve = pbar.querySelector('[data-erc-pbar-reserve]');
    var reserveLabel = pbar.querySelector('[data-erc-pbar-label]');
    var reserveText = reserveLabel ? reserveLabel.textContent.trim() : '';
    var priceOut = pbar.querySelector('[data-erc-pbar-price]');
    var focusables = pbar.querySelectorAll('a, button');
    var infoBox = document.querySelector('#MainContent .product__info-container') || document.getElementById('MainContent');

    var setBar = function (on) {
      if (pbar.classList.contains('is-visible') === on) return;
      pbar.classList.toggle('is-visible', on);
      pbar.setAttribute('aria-hidden', on ? 'false' : 'true');
      focusables.forEach(function (el) {
        el.tabIndex = on ? 0 : -1;
      });
      root.classList.toggle('erc-pbar-on', on);
      if (on) root.style.setProperty('--erc-pbar-h', pbar.offsetHeight + 'px');
    };

    var mobileMq = window.matchMedia('(max-width: 749px)');
    var scrolledPast = false;
    var updateBar = function () {
      setBar(mobileMq.matches || scrolledPast);
    };
    if (mobileMq.addEventListener) {
      mobileMq.addEventListener('change', updateBar);
    } else if (mobileMq.addListener) {
      mobileMq.addListener(updateBar);
    }

    var target = null;
    var targetIo = null;
    var targetMo = null;
    var appointoText = '';

    var isAppointo = function (el) {
      return !!el && /^appointo-btn/.test(el.id || '');
    };

    var inContent = function (el) {
      var main = document.getElementById('MainContent');
      return !!main && main.contains(el);
    };

    var findTarget = function () {
      return (
        document.getElementById('appointo-btn') ||
        document.querySelector('#MainContent [id^="appointo-btn-"]') ||
        document.querySelector('#MainContent .product-form__submit[name="add"]')
      );
    };

    var syncReserve = function () {
      if (!reserve) return;
      if (!target) {
        reserve.disabled = false;
        if (reserveLabel) reserveLabel.textContent = reserveText;
        return;
      }
      if (isAppointo(target)) {
        // Appointo gestiona la disponibilidad en su calendario: nunca deshabilitar.
        reserve.disabled = false;
        if (reserveLabel) reserveLabel.textContent = appointoText || reserveText;
        return;
      }
      var disabled = target.disabled || target.getAttribute('aria-disabled') === 'true';
      reserve.disabled = disabled;
      if (reserveLabel) {
        var realText = (target.querySelector('span') || target).textContent.trim();
        reserveLabel.textContent = disabled && realText ? realText : reserveText;
      }
    };

    var bindTarget = function () {
      var next = findTarget();
      if (next === target) return;
      target = next;
      if (targetIo) {
        targetIo.disconnect();
        targetIo = null;
      }
      if (targetMo) targetMo.disconnect();
      scrolledPast = false;

      if (target) {
        // Texto configurado en Appointo (luego cambia a "Loading Calendar..." al pulsar).
        if (isAppointo(target)) appointoText = target.textContent.trim();
        // El botón flotante de Appointo (.appointo-check-avail) no está en la ficha:
        // en ese caso la visibilidad en escritorio va por scroll (ver más abajo).
        if ('IntersectionObserver' in window && inContent(target)) {
          targetIo = new IntersectionObserver(function (entries) {
            var e = entries[0];
            scrolledPast = !e.isIntersecting && e.boundingClientRect.top < 0;
            updateBar();
          });
          targetIo.observe(target);
        }
        if (!isAppointo(target)) {
          targetMo = new MutationObserver(syncReserve);
          targetMo.observe(target, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
            childList: true,
            subtree: true,
            characterData: true
          });
        }
      }
      syncReserve();
      updateBar();
    };

    bindTarget();

    // Appointo carga después y reemplaza el botón: volver a enlazar cuando cambie el formulario.
    if (infoBox && 'MutationObserver' in window) {
      var rebindQueued = false;
      new MutationObserver(function () {
        if (rebindQueued) return;
        rebindQueued = true;
        window.requestAnimationFrame(function () {
          rebindQueued = false;
          bindTarget();
        });
      }).observe(infoBox, { childList: true, subtree: true });
    }

    // Sin botón de reserva en la ficha: en escritorio, tras 600 px de scroll.
    window.addEventListener(
      'scroll',
      function () {
        if (targetIo) return;
        scrolledPast = (window.scrollY || window.pageYOffset) > 600;
        updateBar();
      },
      { passive: true }
    );

    if (reserve) {
      reserve.addEventListener('click', function () {
        bindTarget();
        var behavior = reduceMotion ? 'auto' : 'smooth';
        if (target && isAppointo(target)) {
          // Abre el modal de disponibilidad de Appointo directamente.
          target.click();
        } else if (target && !target.disabled) {
          target.click();
        } else {
          var form = target || document.querySelector('#MainContent product-form, #MainContent .product__info-container');
          if (form) form.scrollIntoView({ behavior: behavior, block: 'center' });
        }
      });
    }

    // Precio: Dawn reemplaza #price-<sección> al cambiar de variante.
    var priceBox = document.querySelector('#MainContent [id^="price-template"]');
    if (priceBox && priceOut) {
      var syncPrice = function () {
        var sale = priceBox.querySelector('.price--on-sale');
        var item = priceBox.querySelector(
          sale ? '.price__sale .price-item--last' : '.price__regular .price-item--regular'
        );
        var text = item && item.textContent.trim();
        if (text) priceOut.textContent = text;
      };
      new MutationObserver(syncPrice).observe(priceBox, { childList: true, subtree: true });
      syncPrice();
    }
  }

})();
